import axios from 'axios';
import { Auth } from 'aws-amplify';
import type { ApiGameListResponse, PicksFilterValues } from './types';

// Hand-typed against the backend at /api/v1/games. Backend lives in
// backend/src/api/v1/games. Source of truth for the shape is that file.
//
// URL resolution:
//   1. If REACT_APP_API_BASE_URL is set at build time, use it (override).
//   2. In production builds, default to '' so axios sends relative URLs.
//      The deployed app's CloudFront distribution routes /api/* to the
//      backend Lambda — same origin, no CORS.
//   3. In local dev (CRA dev server), default to http://localhost:4001
//      where `npm run start` in /backend listens.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ??
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4001');

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10_000,
});

// Pull the active Cognito ID token (sub claim, group membership) on every
// request. Falls back silently if no session exists — the request will hit
// the API and get a 401, which the page can surface to the user / kick to
// the Authenticator sign-in flow.
client.interceptors.request.use(async (config) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    config.headers = config.headers ?? {};
    // axios v0.27 headers are AxiosRequestHeaders | undefined — assignment
    // is safe here once we've defaulted.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config.headers as any).Authorization = `Bearer ${token}`;
  } catch {
    // No active session — let the request go through unauthenticated. The
    // server will respond 401 and React UI can prompt sign-in.
  }
  return config;
});

const DEFAULT_BATCH_SIZE = 25;

export type ListGamesParams = PicksFilterValues & {
  batchSize?: number;
  pageNum?: number;
};

export const listGames = async (params: ListGamesParams): Promise<ApiGameListResponse> => {
  const query: Record<string, string | number> = {
    sport: params.sport,
    batchSize: params.batchSize ?? DEFAULT_BATCH_SIZE,
    pageNum: params.pageNum ?? 1,
  };
  const res = await client.get<ApiGameListResponse>('/api/v1/games', { params: query });
  return res.data;
};
